// @flow

import * as React from 'react';
import { Box, useSystem } from 'react-system';
import { graphql } from 'react-relay';
import Head from 'next/head';
import { type NextQuery, nextQuery } from '../controls/relay';
import { DescriptorStore } from '../controls/router';
import { MuiThemeProvider } from '../controls/mui-providers';
import { Layout } from '../shared/Layout.js';
import { useViewportType } from '../hooks/viewport-type';
import { useLocale } from '../hooks/locale';
import { LISTINGS_LIST_MAX_WIDTH } from '../shared/AggregatesListings/consts';
import { TopFiltersCutomPage } from '../shared/AggregatesListings/Filters/TopFilters.js';
import { AggregatesListingsCustomPage } from '../shared/AggregatesListings/AggregatesListingsCustomPage';
import {
  listingsDescriptor,
  paramsToVariables,
  useFiltersToParams,
} from '../shared/AggregatesListings/listingsDescriptor';
import { ListingsStateProvider } from '../shared/AggregatesListings/state';
import {
  showMapDescriptor,
  offerTypeFilterDescriptor,
} from '../shared/AggregatesListings/Filters/sharedDescriptors';
import { AggregatesListingDialogClient } from '../shared/AggregatesListings/aggregates-listing-dialog-client';
import { useRouter } from '../controls/router/RouterContext';
import type { popularSearchesQuery } from './__generated__/popularSearchesQuery.graphql';

const ListingsLayoutHeaderContent = ({ root, page }) => {
  const { media } = useSystem();
  const LOGO_DESKTOP_WIDTH = 160;
  const PADDING = 16;
  // I have no idea why 172px works, as based on my knowledge
  // 176 = LOGO_DESKTOP_WIDTH + PADDINGS should work better, but it's not.
  // Visually on all browsers 172 gives 10x better result vs 176
  // What happens with 4px and where they come from I don't know
  const HAND_PICKED_UP_COST = 172;

  return (
    <Box
      flexShrink={0}
      pr={3}
      css={media({
        // Max width should be aligned when max width of listings card achieved
        maxWidth: LISTINGS_LIST_MAX_WIDTH - LOGO_DESKTOP_WIDTH - PADDING * 2,

        width: `calc(62% - ${HAND_PICKED_UP_COST}px)`,
        display: ['none', 'block'],
        // Height of underlying input, needed to align underlying content
        // using top of this box.
        height: '54px',
      })}
    >
      <TopFiltersCutomPage root={root} page={page} />
    </Box>
  );
};

const AggregatesListingsCustomPageWithProviders = props => {
  const filterParams = useFiltersToParams(
    props.data.aggregatorCustomPageBySlug?.filters,
  );

  return (
    <ListingsStateProvider>
      <DescriptorStore filterProps={filterParams}>
        <CustomListingsPage {...props} filterProps={filterParams} />
      </DescriptorStore>
    </ListingsStateProvider>
  );
};

const CustomListingsPage = props => {
  const viewportType = useViewportType();
  const [{ showMap }] = showMapDescriptor.useState();
  const [{ offerType }] = offerTypeFilterDescriptor.useState();
  const [params, setParams] = listingsDescriptor.useState();
  const { interpolate } = useLocale();
  const router = useRouter();

  // SET descriptor store for predefined filters queried for custom listing
  React.useEffect(() => {
    setParams(props.filterProps);
    // eslint-disable-next-line
  }, [router.query]);

  const buyLogoLink = props.data.cms.navigation?.nav_listings_buy_logo_link;
  const rentLogoLink = props.data.cms.navigation?.nav_listings_rent_logo_link;

  const getLocalizedQuery = mapPlace => {
    const { places } = props.data.aggregatorCustomPageBySlug?.filters ?? {};

    const { offerType, compositePropertyType_eq, ...rest } = props.filterProps;

    return places == null || places.length === 0
      ? {
          ...rest,
          placeSlugs: null,
          compositePropertyType_eq: JSON.stringify(compositePropertyType_eq),
          offerType: JSON.stringify(offerType),
        }
      : {
          ...rest,
          placeSlugs: JSON.stringify(places.map(mapPlace)),
          compositePropertyType_eq: JSON.stringify(compositePropertyType_eq),
          offerType: JSON.stringify(offerType),
        };
  };

  const buyLogoHref =
    buyLogoLink?._meta?.uid != null && buyLogoLink.template != null
      ? {
          pathname: `/${buyLogoLink.template}`,
          query: {
            uid: buyLogoLink._meta.uid,
          },
        }
      : null;

  const rentLogoHref =
    rentLogoLink?._meta?.uid != null && rentLogoLink.template != null
      ? {
          pathname: `/${rentLogoLink.template}`,
          query: {
            uid: rentLogoLink._meta.uid,
          },
        }
      : null;

  return (
    <MuiThemeProvider>
      <Layout
        root={props.data}
        pageWithData={props.data.cms.pageWithData}
        headerStyle="listings-page"
        headerContent={
          <ListingsLayoutHeaderContent
            root={props.data}
            page={props.data.cms.pageWithData?.page}
          />
        }
        showFooter={
          showMap === false || (showMap == null && viewportType === 'mobile')
        }
        logoHref={offerType === 'buy' ? buyLogoHref : rentLogoHref}
        localeSwitcherQuery={{
          en: getLocalizedQuery(p => ({
            type: p.type,
            slug: p.slugEn,
          })),
          fr: getLocalizedQuery(p => ({
            type: p.type,
            slug: p.slugFr,
          })),
          de: getLocalizedQuery(p => ({
            type: p.type,
            slug: p.slugDe,
          })),
          it: getLocalizedQuery(p => ({
            type: p.type,
            slug: p.slugIt,
          })),
          es: getLocalizedQuery(p => ({
            type: p.type,
            slug: p.slugEs,
          })),
        }}
        customCountrySwitcherHref={{ pathname: '/', query: {} }}
      >
        <Head>
          <title>
            {interpolate(
              props.data.aggregatorCustomPageBySlug?.pageTitle ?? '',
              {
                totalCount:
                  props.data.aggregatorCustomPageBySlug?.aggregatesListings
                    ?.totalCount ?? 0,
              },
            )}
          </title>
          <meta
            name="description"
            content={props.data.aggregatorCustomPageBySlug?.pageMeta}
          />
          <style>{`html { overflow-y: scroll; }`}</style>
        </Head>

        <AggregatesListingsCustomPage
          root={props.data}
          page={props.data.cms.pageWithData?.page}
        />

        {props.data.aggregatorCustomPageBySlug?.aggregatesListings && (
          <AggregatesListingDialogClient
            parentHref={props.router}
            params={params}
            aggregatesListings={
              props.data.aggregatorCustomPageBySlug.aggregatesListings
            }
          />
        )}
      </Layout>
    </MuiThemeProvider>
  );
};

const PopularSearchesPage: NextQuery<
  popularSearchesQuery,
  {|
    slug: string,
    lang?: string,
    uid?: string,
  |},
> = nextQuery(ctx => {
  const params = listingsDescriptor.getState(ctx.query);
  const {
    includePlace,
    lngShort,
    includeEn,
    includeFr,
    includeIt,
    includeDe,
    includeEs,
    propertyMainType,
    numberOfRooms,
    offerType,
    ...otherParams
  } = paramsToVariables(params, ctx.query);

  return {
    query: graphql`
      query popularSearchesQuery(
        $sortBy: String
        $sortDirection: String
        $filters: AggregatesListingFiltersInput!
        $page: AggregatesListingFiltersPage
        $slug: String!
        $includeCountry: Boolean!
        $uid: String!
        $lang: String!
      ) {
        ...Layout_root @arguments(lang: $lang)
        ...AggregatesListingsCustomPage_root
        ...TopFiltersCustomPage_root

        aggregatorCustomPageBySlug(slug: $slug) {
          pageTitle
          pageMeta
          filters {
            places {
              type
              slug
              slugEn: slug(lang: en)
              slugFr: slug(lang: fr)
              slugDe: slug(lang: de)
              slugIt: slug(lang: it)
              slugEs: slug(lang: es)
            }
            ...listingsDescriptorFiltersToParams_filters
          }

          aggregatesListings: listings {
            totalCount
            ...aggregatesListingDialogClient_aggregatesListings
          }
        }

        cms {
          pageWithData(uid: $uid, lang: $lang) {
            page {
              ...TopFiltersCustomPage_page
              ...AggregatesListingsCustomPage_page
            }
            ...Layout_pageWithData
          }

          navigation(uid: "main", lang: $lang) {
            nav_listings_buy_logo_link {
              ... on CMSPage {
                template
                _meta {
                  uid
                }
              }
            }
            nav_listings_rent_logo_link {
              ... on CMSPage {
                template
                _meta {
                  uid
                }
              }
            }
          }
        }
      }
    `,
    variables: {
      ...otherParams,
      slug: ctx.query.slug,
    },
    cacheStrategy: 'none',
    validate: data => {
      // we want to show 404 if the slug is not found.
      if (data.aggregatorCustomPageBySlug == null) {
        return { type: 'error', statusCode: 404 };
      }
      return null;
    },
  };
})(AggregatesListingsCustomPageWithProviders);

export default PopularSearchesPage;
